// Abgebrochene Funnel-Leads mit Business-Mail.
//
// Der Funnel (aivy-customer-dashboard, funnel-q4.js) legt beim Öffnen einen
// Form-Eintrag mit status IN_PROGRESS an und schreibt data.EMAIL nur, wenn die
// Adresse den Freemail-Check bestanden hat. Abgeschlossen ist ein Lead erst
// mit status RECEIVED (danach ggf. ASSIGNED/PROCESSED/COMPLETED). Auch die
// Einbindung auf landingpage.aivy.app ist derselbe Funnel per iFrame.

export const FUNNEL_FORM_SOURCE = 'FUNNEL_LEAD_QUALIFICATION'

const INTERNAL_DOMAINS = ['aivy.app', 'aivy-app.de']

// Schritte aus funnel-q4.js: 0 WELCOME · 1 CREATING · 2 Q1 · 3 Q2 · 4 Q3 · 5 Q4
const ACTIVITY_LABELS = {
  STEP_0_VIEWED: 'E-Mail-Eingabe',
  STEP_1_VIEWED: 'Account-Erstellung',
  STEP_2_VIEWED: 'Q1 Jobs',
  STEP_3_VIEWED: 'Q2 Volumen',
  STEP_4_VIEWED: 'Q3 Hiring',
  STEP_5_VIEWED: 'Q4 ATS'
}

export const getActivityLabel = (lastActivity) =>
  ACTIVITY_LABELS[lastActivity] || lastActivity || '–'

export const parseData = (data) => {
  if (!data) return {}
  if (typeof data === 'object') return data
  try {
    return JSON.parse(data) || {}
  } catch (_) {
    return {}
  }
}

export const normalizeEmail = (email) =>
  typeof email === 'string' ? email.trim().toLowerCase() : ''

export const isInternal = (email) =>
  INTERNAL_DOMAINS.includes(email.split('@').pop())

const getHost = (url) => {
  try {
    return new URL(url).host
  } catch (_) {
    return null
  }
}

export const getSource = (funnelContext = {}) => {
  const host = getHost(funnelContext.referrer)
  const parts = [host || 'direkt']
  if (funnelContext.source) parts.push(funnelContext.source)
  let label = parts.join(' · ')
  if (funnelContext.embedded) label += ' (iFrame)'
  return label
}

const getActivityTime = (form) =>
  form.last_activity_time || form.updatedAt || form.createdAt

export const getFunnelDropouts = (forms = []) => {
  const finishedEmails = new Set()
  const byEmail = new Map()

  for (const form of forms) {
    if (form.form_source !== FUNNEL_FORM_SOURCE) continue
    const data = parseData(form.data)
    const email = normalizeEmail(data.EMAIL)
    if (!email) continue

    if (form.status !== 'IN_PROGRESS') {
      finishedEmails.add(email)
      continue
    }

    const followup = data.followup?.contacted_at
      ? { ...data.followup, form_id: form.id }
      : null

    const entry = byEmail.get(email)
    if (!entry) {
      byEmail.set(email, { form, data, attempts: 1, followup })
      continue
    }
    entry.attempts += 1
    if (getActivityTime(form) > getActivityTime(entry.form)) {
      entry.form = form
      entry.data = data
    }
    // Markierung kann an einem älteren Versuch hängen; gilt für die ganze Mail.
    if (
      followup &&
      (!entry.followup || followup.contacted_at > entry.followup.contacted_at)
    ) {
      entry.followup = followup
    }
  }

  return [...byEmail.entries()]
    .filter(([email]) => !finishedEmails.has(email) && !isInternal(email))
    .map(([email, { form, data, attempts, followup }]) => ({
      id: form.id,
      email,
      company: data.brand_name || null,
      employees: data.brand_employees || null,
      last_activity: form.last_activity || null,
      activity_label: getActivityLabel(form.last_activity),
      lead_progress: form.lead_progress ?? null,
      activity_time: getActivityTime(form),
      createdAt: form.createdAt,
      attempts,
      language: data.language || null,
      source: getSource(data.funnel_context),
      ref_link: form.ref_link || data.ref_link || null,
      contacted_at: followup?.contacted_at || null,
      contacted_by: followup?.contacted_by || null,
      followup_form_id: followup?.form_id || null
    }))
    .sort((a, b) => (a.activity_time < b.activity_time ? 1 : -1))
}

export const filterByDays = (dropouts, days, now = new Date()) => {
  if (!days) return dropouts
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
  return dropouts.filter((d) => new Date(d.activity_time) >= from)
}

export const filterByFollowup = (dropouts, followup) => {
  if (followup === 'open') return dropouts.filter((d) => !d.contacted_at)
  if (followup === 'contacted') return dropouts.filter((d) => d.contacted_at)
  return dropouts
}

// Setzt oder entfernt die Kontaktiert-Markierung, ohne andere Felder in data
// anzufassen. Der Aufrufer muss data frisch laden: updateForm ersetzt das
// komplette AWSJSON-Feld.
export const withFollowup = (rawData, followup) => {
  const { followup: _previous, ...rest } = parseData(rawData)
  return followup ? { ...rest, followup } : rest
}
